import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, APP_STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import ReassignModal from "@/components/ReassignModal";
import AdminCancelPlacementModal from "@/components/AdminCancelPlacementModal";
import MobileFilterRail from "@/components/MobileFilterRail";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Users, Eye, Building2, MapPin, Pencil, ArrowLeftRight, X } from "lucide-react";

const MODE_LABEL = { onsite: "حضوري", remote: "عن بُعد", hybrid: "هجين" };

export default function ApplicationsAdmin() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [mentorId, setMentorId] = useState("");
  const [overriding, setOverriding] = useState({});
  const [reassign, setReassign] = useState(null); // { app, mode }
  const [cancelFor, setCancelFor] = useState(null); // application being admin-cancelled

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications");
      setApps(data.applications);
      if (user.role === "super_admin") {
        const [a, m] = await Promise.all([
          api.get("/directory?role=association"),
          api.get("/directory?role=mentor"),
        ]);
        setAssociations(a.data.users);
        setMentors(m.data.users);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const decide = async (id, patch) => {
    try {
      await api.patch(`/applications/${id}`, patch);
      toast.success("تم التحديث");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const mentorStats = useMemo(() => {
    const associationById = new Map(associations.map((association) => [association.id, association.organization_name || association.name]));
    return mentors.map((mentor) => {
      const assigned = apps.filter((app) => app.assigned_mentor_id === mentor.id && ["accepted", "assigned"].includes(app.status));
      const associationCounts = assigned.reduce((counts, app) => {
        const id = app.assigned_association_id;
        if (id) counts.set(id, (counts.get(id) || 0) + 1);
        return counts;
      }, new Map());
      return {
        ...mentor,
        studentCount: assigned.length,
        associations: [...associationCounts].map(([id, count]) => ({ id, name: associationById.get(id) || "جمعية غير معروفة", count })),
      };
    });
  }, [apps, associations, mentors]);

  const selectedMentor = mentorStats.find((mentor) => mentor.id === mentorId);
  const filtered = useMemo(() => {
    let list = filter ? apps.filter((a) => a.status === filter) : apps;
    if (mentorId) list = list.filter((a) => a.assigned_mentor_id === mentorId);
    return list;
  }, [apps, filter, mentorId]);

  const isAdmin = user.role === "super_admin";

  return (
    <DashboardLayout>
      <div data-testid="applications-admin">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إدارة تسكين الطلاب</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">
            {isAdmin ? "إدارة تسكين الطلاب" : "الطلاب الذين تم تسكينهم"}
          </h1>
        </div>

        <MobileFilterRail
          value={filter}
          onChange={(id) => setFilter(id)}
          filters={useMemo(() => {
            const counts = apps.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] || 0) + 1 }), {});
            return [
              { id: "", label: "الكل", count: apps.length },
              ...Object.entries(APP_STATUS_LABELS).map(([k, v]) => ({ id: k, label: v, count: counts[k] || 0 })),
            ];
          }, [apps])}
        />

        {isAdmin && (
          <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4">
            <label className="block max-w-xl text-sm font-bold text-slate-700">
              فلترة كاملة باسم الموجّه
              <select
                value={mentorId}
                onChange={(e) => setMentorId(e.target.value)}
                className="mt-1.5 min-h-11 w-full rounded-xl border-2 border-slate-200 bg-white px-3 text-sm outline-none transition-all focus:border-teal-600 focus:ring-4 focus:ring-teal-600/10"
                data-testid="apps-admin-mentor-filter"
              >
                <option value="">كل الموجّهين ({apps.filter((app) => app.assigned_mentor_id).length} طلب)</option>
                {mentorStats.map((mentor) => (
                  <option key={mentor.id} value={mentor.id}>{mentor.name} ({mentor.studentCount} طالب)</option>
                ))}
              </select>
            </label>
            {selectedMentor && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="selected-mentor-summary">
                <span className="text-sm font-black text-slate-900">{selectedMentor.studentCount} طالب</span>
                {selectedMentor.associations.length > 0 ? selectedMentor.associations.map((association) => (
                  <span key={association.id} className="rounded-lg bg-teal-50 px-2.5 py-1 text-xs font-bold text-teal-800">
                    {association.name} · {association.count}
                  </span>
                )) : <span className="text-xs text-slate-500">لا توجد جمعية مسكّنة لهذا الموجّه</span>}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 rounded-2xl bg-white border border-slate-200 text-center">
            <Users className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <div className="text-slate-600">لا توجد طلبات</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((a) => (
              <div key={a.id} className="p-4 bg-white rounded-2xl border border-slate-200 card-shadow" data-testid={`app-${a.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-bold text-slate-900">{a.student_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5" dir="ltr">{a.student_email}</div>
                    {a.opportunity && (
                      <div className="mt-2 text-sm text-teal-700 font-medium">{a.opportunity.title}</div>
                    )}
                    {(a.preferred_association_name || a.execution_city || a.execution_delivery_mode) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`app-preference-${a.id}`}>
                        {a.preferred_association_name && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-bold text-teal-800 ring-1 ring-teal-100">
                            <Building2 className="h-3 w-3" /> {a.preferred_association_name}
                          </span>
                        )}
                        {a.execution_city && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">
                            <MapPin className="h-3 w-3" /> {a.execution_city}
                          </span>
                        )}
                        {a.execution_delivery_mode && (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                              a.execution_delivery_mode === "remote"
                                ? "bg-sky-50 text-sky-700"
                                : a.execution_delivery_mode === "hybrid"
                                ? "bg-violet-50 text-violet-700"
                                : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {MODE_LABEL[a.execution_delivery_mode] || a.execution_delivery_mode}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold shrink-0
                    ${a.status === "accepted" || a.status === "assigned" ? "bg-emerald-50 text-emerald-700"
                      : a.status === "rejected" ? "bg-red-50 text-red-700"
                      : a.status === "under_review" ? "bg-sky-50 text-sky-700"
                      : "bg-amber-50 text-amber-700"}`}>
                    {APP_STATUS_LABELS[a.status]}
                  </span>
                </div>

                {a.motivation && (
                  <div className="mt-4 p-3 rounded-lg bg-slate-50 text-sm text-slate-700">
                    <span className="font-semibold">الدوافع:</span> {a.motivation}
                  </div>
                )}
                {a.experience && (
                  <div className="mt-2 p-3 rounded-lg bg-slate-50 text-sm text-slate-700">
                    <span className="font-semibold">الخبرة:</span> {a.experience}
                  </div>
                )}

                {isAdmin && ["accepted", "assigned"].includes(a.status) && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Link to={`/dashboard/admin/journeys/${a.id}`} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white" data-testid={`open-journey-${a.id}`}>
                      <Eye className="h-3.5 w-3.5" /> فتح الرحلة التشغيلية
                    </Link>
                    <button
                      type="button"
                      onClick={() => setReassign({ app: a, mode: "association" })}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-bold text-teal-800 hover:bg-teal-100"
                      data-testid={`btn-reassign-assoc-${a.id}`}
                    >
                      <ArrowLeftRight className="h-3.5 w-3.5" /> تغيير الجمعية
                    </button>
                    <button
                      type="button"
                      onClick={() => setReassign({ app: a, mode: "mentor" })}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-800 hover:bg-indigo-100"
                      data-testid={`btn-reassign-mentor-${a.id}`}
                    >
                      <ArrowLeftRight className="h-3.5 w-3.5" /> تغيير الموجّه
                    </button>
                    <button
                      type="button"
                      onClick={() => setCancelFor(a)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-800 hover:bg-rose-100"
                      data-testid={`btn-admin-cancel-${a.id}`}
                      title="إلغاء التسكين مباشرة بصلاحية الإدارة"
                    >
                      <X className="h-3.5 w-3.5" /> إلغاء التسكين
                    </button>
                  </div>
                )}
                {isAdmin && (
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {a.preferred_association_id && !overriding[a.id] && !a.assigned_association_id ? (
                        <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2" data-testid={`preferred-assoc-${a.id}`}>
                          <div className="flex items-center gap-2 text-sm">
                            <Building2 className="h-4 w-4 text-emerald-700" />
                            <div>
                              <div className="text-xs text-emerald-700 font-bold">اختيار الطالب</div>
                              <div className="text-sm font-semibold text-slate-900">{a.preferred_association_name}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setOverriding((o) => ({ ...o, [a.id]: true }))}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                            data-testid={`override-assoc-${a.id}`}
                            title="تغيير الجمعية يدوياً"
                          >
                            <Pencil className="h-3 w-3" /> تغيير
                          </button>
                        </div>
                      ) : (
                        <select
                          value={a.assigned_association_id || ""}
                          onChange={(e) => decide(a.id, { status: a.status, assigned_association_id: e.target.value })}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                          data-testid={`assign-assoc-${a.id}`}
                        >
                          <option value="">
                            {a.preferred_association_id ? "تعديل الجمعية…" : "اختر جمعية"}
                          </option>
                          {associations.map((u) => <option key={u.id} value={u.id}>{u.organization_name || u.name}</option>)}
                        </select>
                      )}
                      <select
                        value={a.assigned_mentor_id || ""}
                        onChange={(e) => decide(a.id, { status: a.status, assigned_mentor_id: e.target.value })}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                        data-testid={`assign-mentor-${a.id}`}
                      >
                        <option value="">اختر موجّه</option>
                        {mentors.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                    {["pending", "under_review"].includes(a.status) && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => decide(a.id, { status: "under_review" })}
                        className="px-3 py-2 rounded-lg text-xs font-semibold bg-sky-50 text-sky-700 hover:bg-sky-100"
                        data-testid={`btn-review-${a.id}`}
                      >قيد المراجعة</button>
                      <button
                        onClick={() => {
                          // If admin didn't override and student picked a preferred association, auto-assign it.
                          const effectiveAssoc = a.assigned_association_id
                            || (!overriding[a.id] && a.preferred_association_id)
                            || null;
                          decide(a.id, {
                            status: effectiveAssoc ? "assigned" : "accepted",
                            ...(effectiveAssoc && !a.assigned_association_id
                              ? { assigned_association_id: effectiveAssoc }
                              : {}),
                          });
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        data-testid={`btn-accept-${a.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> قبول
                      </button>
                      <button
                        onClick={() => decide(a.id, { status: "rejected" })}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100"
                        data-testid={`btn-reject-${a.id}`}
                      >
                        <XCircle className="h-3.5 w-3.5" /> رفض
                      </button>
                    </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {reassign && (
        <ReassignModal
          app={reassign.app}
          mode={reassign.mode}
          options={
            reassign.mode === "association"
              ? associations.map((u) => ({ id: u.id, name: u.organization_name || u.name }))
              : mentors.map((u) => ({ id: u.id, name: u.name }))
          }
          onClose={() => setReassign(null)}
          onSaved={() => {
            setReassign(null);
            load();
          }}
        />
      )}
      {cancelFor && (
        <AdminCancelPlacementModal
          app={cancelFor}
          onClose={() => setCancelFor(null)}
          onCancelled={() => { setCancelFor(null); load(); }}
        />
      )}
    </DashboardLayout>
  );
}
